import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSupabaseClient } from '../../lib/supabase';
import type { GuideDetails } from '../../services/matchingService';
import { ConsultationScheduler } from '../booking/ConsultationScheduler';

export function ConnectBookingPage() {
  const navigate = useNavigate();
  const [guide, setGuide] = useState<GuideDetails | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);

  const supabase = useMemo(() => {
    try {
      return getSupabaseClient();
    } catch (err) {
      console.error('Failed to init supabase client', err);
      return null;
    }
  }, []);

  useEffect(() => {
    const guideId = localStorage.getItem('direct_connect_guide_id');
    if (!guideId || !supabase) {
      setError('We could not find the guide associated with this invitation.');
      setStatus('error');
      return;
    }

    let isActive = true;

    async function loadGuideSnapshot() {
      try {
        const { data: authData, error: authError } = await supabase.auth.getUser();
        if (authError || !authData?.user) {
          navigate('/login', { replace: true });
          return;
        }

        // Use edge function to fetch guide snapshot (RLS-compliant)
        const { data: snapshotData, error: snapshotError } = await supabase.functions.invoke(
          'direct-connect-guide-snapshot',
          {
            body: { guide_id: guideId },
          }
        );

        if (snapshotError) {
          console.error('Failed to load guide snapshot via edge function', snapshotError);
          throw snapshotError;
        }

        if (!snapshotData?.guide) {
          throw new Error('Guide not found');
        }

        const guideData = snapshotData.guide;

        if (isActive) {
          setGuide({
            guide_id: guideData.guide_id,
            name: guideData.name ?? 'Your Guide',
            location: guideData.location,
            bio: guideData.bio,
            experience_types: guideData.experience_types,
            support_types: guideData.support_types,
            focus_areas: guideData.focus_areas,
            special_groups: guideData.special_groups,
            religious_beliefs: guideData.religious_beliefs,
            backgroundChecked: guideData.background_checked,
          });
          setStatus('ready');
        }
      } catch (err) {
        console.error('Failed to load guide snapshot', err);
        if (isActive) {
          setError('We had trouble loading availability for this guide. Please try again later.');
          setStatus('error');
        }
      }
    }

    void loadGuideSnapshot();

    return () => {
      isActive = false;
    };
  }, [supabase, navigate]);

  const handleBookingComplete = () => {
    localStorage.removeItem('direct_connect_active');
    navigate('/home', { replace: true });
  };

  const handleBack = () => {
    navigate('/profile');
  };

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] text-[#FEF6D6]">
        <div className="text-center space-y-4">
          <div className="mx-auto h-12 w-12 rounded-full border-2 border-[#FEF6D6]/20 border-t-[#FEF6D6] animate-spin" />
          <p className="text-sm text-[#FEF6D6]/70">Loading availability…</p>
        </div>
      </div>
    );
  }

  if (status === 'error' || !guide) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#2C2731] px-4 text-[#FEF6D6]">
        <div className="w-full max-w-md rounded-2xl border border-[#FEF6D6]/20 bg-[#2C2731] p-8 text-center shadow-xl shadow-black/30">
          <h1 className="text-2xl font-semibold">We hit a snag</h1>
          <p className="mt-3 text-sm text-[#FEF6D6]/80">{error || 'We couldn’t load your booking options right now.'}</p>
          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-6 w-full rounded-full border border-[#FEF6D6]/40 px-4 py-2 text-sm font-semibold text-[#FEF6D6] transition hover:bg-[#FEF6D6]/10"
          >
            Return home
          </button>
        </div>
      </div>
    );
  }

  return (
    <ConsultationScheduler
      guide={guide}
      onBack={handleBack}
      onBookingComplete={handleBookingComplete}
      accountAlreadyCreated
    />
  );
}
